// src/components/common/ui.jsx
//
// The shared vocabulary every screen is built from. Colours come from the
// tokens in index.css, so nothing in here needs a `dark:` variant.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2 } from 'lucide-react';

export const cx = (...parts) => parts.filter(Boolean).join(' ');

/* ---------------------------------------------------------------- Buttons */

const BUTTON_VARIANTS = {
  primary: 'bg-accent text-accent-ink border-accent hover:brightness-110',
  secondary: 'bg-surface text-ink border-line-strong hover:bg-surface-2',
  ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-surface-2 hover:text-ink',
  danger: 'bg-danger-soft text-danger border-transparent hover:brightness-95',
};

const BUTTON_SIZES = {
  md: 'px-3.5 py-2 text-sm gap-2',
  sm: 'px-3 py-1.5 text-[13px] gap-1.5',
};

export const Button = ({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  className,
  children,
  ...props
}) => (
  <button
    type={type}
    className={cx(
      'inline-flex items-center justify-center rounded-lg border font-medium',
      'transition-colors disabled:opacity-50 disabled:pointer-events-none',
      BUTTON_VARIANTS[variant],
      BUTTON_SIZES[size],
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export const IconButton = ({ label, tone = 'default', className, children, ...props }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={cx(
      'grid h-8 w-8 place-items-center rounded-lg text-ink-3 transition-colors',
      'disabled:opacity-30 disabled:pointer-events-none',
      tone === 'danger'
        ? 'hover:bg-danger-soft hover:text-danger'
        : 'hover:bg-surface-3 hover:text-ink',
      className
    )}
    {...props}
  >
    {children}
  </button>
);

/* --------------------------------------------------------------- Surfaces */

export const Panel = ({ className, children }) => (
  <div className={cx('overflow-hidden rounded-card border border-line bg-surface', className)}>
    {children}
  </div>
);

const PILL_TONES = {
  neutral: 'bg-surface-2 text-ink-2 border-line',
  accent: 'bg-accent-soft text-accent border-accent-line',
  gold: 'bg-gold-soft text-gold border-transparent',
  ok: 'bg-ok-soft text-ok border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
};

export const Pill = ({ tone = 'neutral', className, children }) => (
  <span
    className={cx(
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs',
      PILL_TONES[tone],
      className
    )}
  >
    {children}
  </span>
);

/* ------------------------------------------------------------ Page header */

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="text-[25px] font-semibold leading-tight">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-ink-2">{subtitle}</p>}
    </div>
    {children}
  </div>
);

/* ------------------------------------------------------------ Form pieces */

const CONTROL = [
  'w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-ink',
  'placeholder:text-ink-3 transition-shadow',
  'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent-soft',
  'disabled:bg-surface-2 disabled:text-ink-3',
].join(' ');

export const Field = ({ label, htmlFor, hint, error, children }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={htmlFor} className="text-[13px] font-medium text-ink">
      {label}
    </label>
    {children}
    {error ? (
      <p className="text-[13px] text-danger">{error}</p>
    ) : (
      hint && <p className="text-xs text-ink-3">{hint}</p>
    )}
  </div>
);

export const TextInput = ({ invalid, className, ...props }) => (
  <input className={cx(CONTROL, invalid && 'border-danger', className)} {...props} />
);

export const TextArea = ({ invalid, className, ...props }) => (
  <textarea className={cx(CONTROL, 'resize-y', invalid && 'border-danger', className)} {...props} />
);

export const Select = ({ invalid, className, children, ...props }) => (
  <select className={cx(CONTROL, 'pr-8', invalid && 'border-danger', className)} {...props}>
    {children}
  </select>
);

export const Checkbox = ({ label, id, description, ...props }) => (
  <div className="flex items-start gap-2.5">
    <input
      id={id}
      type="checkbox"
      className="mt-0.5 h-4 w-4 flex-none rounded border-line-strong text-accent focus:ring-accent"
      {...props}
    />
    <label htmlFor={id} className="text-sm text-ink">
      {label}
      {description && <span className="mt-0.5 block text-xs text-ink-3">{description}</span>}
    </label>
  </div>
);

export const SearchInput = React.forwardRef(({ shortcut, className, ...props }, ref) => (
  <div className={cx('relative flex-1', className)}>
    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
    <input
      ref={ref}
      type="search"
      className={cx(CONTROL, 'py-2.5 pl-10', shortcut && 'pr-16')}
      {...props}
    />
    {shortcut && (
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-line-strong px-1.5 py-0.5 font-mono text-[11px] text-ink-3">
        {shortcut}
      </span>
    )}
  </div>
));
SearchInput.displayName = 'SearchInput';

/* -------------------------------------------------------------- Feedback */

export const Spinner = ({ label = 'Loading…', className }) => (
  <div className={cx('flex flex-col items-center justify-center gap-3 py-16 text-ink-3', className)}>
    <Loader2 className="h-6 w-6 animate-spin" />
    <p className="text-sm">{label}</p>
  </div>
);

export const EmptyState = ({ icon: Icon, title, children, action }) => (
  <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-line-strong px-6 py-14 text-center">
    {Icon && <Icon className="mb-1 h-6 w-6 text-ink-3" />}
    <p className="font-display text-base font-semibold text-ink">{title}</p>
    {children && <p className="max-w-md text-sm text-ink-2">{children}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const ErrorState = ({ title = 'Something went wrong', children, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-card border border-line bg-danger-soft px-6 py-12 text-center">
    <p className="font-display text-base font-semibold text-danger">{title}</p>
    {children && <p className="max-w-md text-sm text-ink-2">{children}</p>}
    {onRetry && (
      <Button className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

/* ----------------------------------------------------------------- Modal */

const MODAL_SIZES = { md: 'max-w-md', lg: 'max-w-xl' };

export const Modal = ({ title, description, size = 'md', onClose, children }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Stop the page behind the dialog from scrolling with it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog so the keyboard lands somewhere sensible.
    const firstField = panelRef.current?.querySelector(
      'input, textarea, select, button'
    );
    firstField?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/50 p-4 py-10 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          'w-full rounded-xl border border-line bg-surface shadow-modal',
          MODAL_SIZES[size]
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] text-ink-2">{description}</p>}
          </div>
          <IconButton label="Close" onClick={onClose} className="-mr-1 -mt-0.5">
            <X className="h-4 w-4" />
          </IconButton>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export const ModalActions = ({ children }) => (
  <div className="mt-6 flex justify-end gap-2.5 border-t border-line pt-4">{children}</div>
);

/* ----------------------------------------------------------------- Table */

export const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] border-collapse">
      <thead>
        <tr>
          {head.map((label, i) => (
            <th
              key={i}
              className={cx(
                'whitespace-nowrap border-b border-line bg-surface-2 px-4 py-2.5',
                'text-[10.5px] font-semibold uppercase tracking-[0.09em] text-ink-3',
                label === '' ? 'text-right' : 'text-left'
              )}
            >
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const Row = ({ children }) => (
  <tr className="border-b border-line last:border-b-0 hover:bg-surface-2">{children}</tr>
);

export const Cell = ({ align = 'left', className, children }) => (
  <td className={cx('px-4 py-3 text-sm align-middle', align === 'right' && 'text-right', className)}>
    {children}
  </td>
);

export const RowActions = ({ children }) => (
  <div className="flex items-center justify-end gap-1">{children}</div>
);
